import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Tooltip,
  Container,
  Avatar,
  Button,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import MedicationLiquidIcon from "@mui/icons-material/MedicationLiquid";
import DomainVerificationIcon from "@mui/icons-material/DomainVerification";
import Face2Icon from "@mui/icons-material/Face2";
import LogoutIcon from "@mui/icons-material/Logout";
import LockResetIcon from "@mui/icons-material/LockReset";
import GroupsIcon from "@mui/icons-material/Groups";
import { API_URL } from "../App";
import { fetchUserData } from "../components/auth";
import { UserDataContext } from "./UserDataContext";
import { apiRequest } from "../components/Config";

function VN() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, setUserData } = useContext(UserDataContext);
  const [expire, setExpire] = useState(null);
  const [loading, setLoading] = useState(true);

  // Stany dla menu
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);

  const handleOpenNavMenu = (event) => setAnchorElNav(event.currentTarget);
  const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);
  const handleCloseNavMenu = () => setAnchorElNav(null);
  const handleCloseUserMenu = () => setAnchorElUser(null);

  const logout = async () => {
    try {
      await apiRequest("logout", "POST");
      setUserData({ userId: 0, user: "", fullName: "" });
      navigate("/login");
    } catch (error) {
      console.error("Błąd podczas wylogowywania:", error);
    }
  };

  useEffect(() => {
    const verifyUser = async () => {
      try {
        const userDataAnswer = await fetchUserData();

        if (userDataAnswer?.userId) {
          setUserData(userDataAnswer);
          setExpire(userDataAnswer.expire);

          // WARUNEK: Jeśli veryf === 1, przekieruj na portfolio/userId
          // Dodano zabezpieczenie, aby nie przekierowywać, jeśli już tam jesteśmy
          if (
            userDataAnswer.veryf === 1 &&
            !location.pathname.startsWith("/portfolio/")
          ) {
            navigate(`/portfolio/${userDataAnswer.userId}`);
            return;
          }
        } else if (
          location.pathname !== "/services" &&
          location.pathname !== "/register" &&
          location.pathname !== "/login"
        ) {
          navigate("/login");
        }
      } catch (error) {
        if (location.pathname !== "/services") navigate("/login");
      } finally {
        setLoading(false);
      }
    };
    verifyUser();
  }, [navigate, setUserData, location.pathname]);

  useEffect(() => {
    if (expire) {
      const today = new Date();
      const input = new Date(expire);
      today.setHours(0, 0, 0, 0);
      input.setHours(0, 0, 0, 0);
      if (input < today) navigate("/expired");
    }
  }, [expire, navigate]);

  const pages = [
    {
      title: "Usługi",
      path: "/services",
      icon: <MedicationLiquidIcon fontSize="small" />,
    },
    { title: "Opiekunki", path: "/list", icon: <Face2Icon fontSize="small" /> },

    // Pokazuj "Mój profil" tylko gdy zalogowany
    ...(userData?.userId
      ? [
          {
            title: "Mój profil",
            path: "/profile",
            icon: <GroupsIcon fontSize="small" />,
          },
        ]
      : []),

    // Pokazuj "Dołącz" tylko gdy NIE zalogowany
    ...(!userData?.userId
      ? [
          {
            title: "Dołącz jako opiekunka",
            path: "/register",
            icon: <DomainVerificationIcon fontSize="small" />,
          },
        ]
      : []),
  ];

  const userPhotoUrl = userData?.userId
    ? `${API_URL}/get-user-photo/${userData.userId}?t=${new Date().getTime()}`
    : null;

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: "rgba(255, 255, 255, 0.85)",
        backdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
        color: "#1a237e",
        top: 0,
        zIndex: 1100,
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ height: 75 }}>
          {/* --- LOGO DESKTOP --- */}
          <IconButton
            onClick={() => navigate("/")}
            sx={{
              display: { xs: "none", md: "flex" },
              mr: 1,
              color: "primary.main",
            }}
          >
            <HomeIcon fontSize="large" />
          </IconButton>

          <Typography
            variant="h6"
            noWrap
            onClick={() => navigate("/")}
            sx={{
              mr: 4,
              display: { xs: "none", md: "flex" },
              fontWeight: 900,
              cursor: "pointer",
              letterSpacing: "-0.5px",
              background: "linear-gradient(45deg, #1a237e 30%, #0d47a1 90%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            TwojaOpiekunka
          </Typography>

          {/* --- MENU MOBILNE --- */}
          <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={anchorElNav}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              PaperProps={{
                sx: {
                  borderRadius: "16px",
                  mt: 1.5,
                  minWidth: 220,
                  boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
                },
              }}
            >
              {pages.map((page) => (
                <MenuItem
                  key={page.title}
                  onClick={() => {
                    navigate(page.path);
                    handleCloseNavMenu();
                  }}
                  selected={location.pathname === page.path}
                  sx={{ py: 1.5 }}
                >
                  <Box sx={{ mr: 2, display: "flex", color: "primary.main" }}>
                    {page.icon}
                  </Box>
                  <Typography fontWeight={600}>{page.title}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* --- LOGO MOBILE --- */}
          <Typography
            variant="h6"
            sx={{
              display: { xs: "flex", md: "none" },
              flexGrow: 1,
              fontWeight: 900,
              color: "primary.main",
            }}
          >
            TO
          </Typography>

          {/* --- NAWIGACJA DESKTOP --- */}
          <Box
            sx={{ flexGrow: 1, display: { xs: "none", md: "flex" }, gap: 1 }}
          >
            {pages.map((page) => {
              const isActive = location.pathname === page.path;
              return (
                <Button
                  key={page.title}
                  onClick={() => navigate(page.path)}
                  startIcon={page.icon}
                  sx={{
                    color: isActive ? "primary.main" : "#444",
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: "10px",
                    fontSize: "0.95rem",
                    px: 2,
                    backgroundColor: isActive
                      ? "rgba(26, 35, 126, 0.06)"
                      : "transparent",
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                  }}
                >
                  {page.title}
                </Button>
              );
            })}
          </Box>

          {/* --- SEKCIJA UŻYTKOWNIKA --- */}
          <Box
            sx={{
              flexGrow: 0,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {!loading && userData?.userId ? (
              <>
                <Box
                  sx={{
                    display: { xs: "none", sm: "block" },
                    textAlign: "right",
                    mr: 1.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 800, color: "#1a237e", lineHeight: 1.1 }}
                  >
                    {userData.fullName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 500 }}
                  >
                    ID: #{userData.userId}
                  </Typography>
                </Box>

                <Tooltip title="Ustawienia profilu">
                  <IconButton
                    onClick={handleOpenUserMenu}
                    sx={{
                      p: "2px",
                      border: "2px solid",
                      borderColor: "primary.light",
                      transition: "0.2s",
                      "&:hover": { transform: "rotate(5deg)" },
                    }}
                  >
                    <Avatar
                      src={userPhotoUrl}
                      alt={userData.fullName}
                      sx={{ width: 38, height: 38, bgcolor: "primary.main" }}
                    >
                      {userData.fullName?.charAt(0)}
                    </Avatar>
                  </IconButton>
                </Tooltip>

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    mx: 1.5,
                    height: 28,
                    my: "auto",
                    display: { xs: "none", sm: "block" },
                  }}
                />

                <Tooltip title="Wyloguj się">
                  <IconButton
                    onClick={logout}
                    sx={{
                      color: "error.main",
                      bgcolor: "rgba(211, 47, 47, 0.06)",
                      borderRadius: "10px",
                      "&:hover": {
                        bgcolor: "rgba(211, 47, 47, 0.12)",
                        transform: "scale(1.05)",
                      },
                      transition: "all 0.2s",
                    }}
                  >
                    <LogoutIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Menu
                  sx={{ mt: "48px" }}
                  anchorEl={anchorElUser}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                  disableScrollLock={true}
                  PaperProps={{
                    sx: {
                      borderRadius: "14px",
                      width: 220,
                      boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                      p: 0.5,
                    },
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      navigate("/passchange");
                      handleCloseUserMenu();
                    }}
                    sx={{ borderRadius: "8px" }}
                  >
                    <LockResetIcon
                      sx={{ mr: 1.5, fontSize: 22, color: "text.secondary" }}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      Zmiana hasła
                    </Typography>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      navigate("/groups");
                      handleCloseUserMenu();
                    }}
                    sx={{ borderRadius: "8px" }}
                  >
                    <GroupsIcon
                      sx={{ mr: 1.5, fontSize: 22, color: "text.secondary" }}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      Zarządzaj grupą
                    </Typography>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              // Przycisk "Zaloguj się"
              // Jeśli nie jesteśmy na /login i nie mamy zalogowanego usera (userId jest 0 lub null)
              location.pathname !== "/login" &&
              !userData?.userId && (
                <Button
                  variant="contained"
                  onClick={() => navigate("/login")}
                  sx={{
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: "0 4px 12px rgba(26, 35, 126, 0.2)",
                    px: 3,
                  }}
                >
                  Zaloguj się
                </Button>
              )
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default VN;
